import { useEffect, useState } from 'react';

/**
 * usePrefersReducedMotion
 *
 * Reactive hook exposing the user's `prefers-reduced-motion` OS setting.
 * Consumers should gate non-essential animations behind its return value.
 *
 * WCAG references:
 *   - 2.3.3 Animation from Interactions
 *
 * Usage:
 *   const reduced = usePrefersReducedMotion();
 *   return <motion.div animate={reduced ? undefined : enterAnim} />;
 *
 * SSR-safe: returns false when `window` is unavailable, then syncs on
 * mount.
 */
export function usePrefersReducedMotion() {
  const getInitial = () => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  const [prefersReduced, setPrefersReduced] = useState(getInitial);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (event) => setPrefersReduced(event.matches);
    setPrefersReduced(mq.matches);
    if (mq.addEventListener) {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    // Safari < 14 fallback
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);

  return prefersReduced;
}

export default usePrefersReducedMotion;
